"use client";

import { SignInButton, SignUpButton, useAuth, useUser } from "@clerk/nextjs";
import {
  ChevronRight,
  HelpCircle,
  Info,
  MapPin,
  ScanLine,
  SwatchBook,
} from "lucide-react";
import Image from "next/image";
import DecksIcon from "modules/common/components/Icons/DecksIcon";
import DiscordIcon from "modules/common/components/Icons/DiscordIcon";
import FacebookIcon from "modules/common/components/Icons/FacebookIcon";
import InstagramIcon from "modules/common/components/Icons/InstagramIcon";
import XIcon from "modules/common/components/Icons/XIcon";
import { DISCORD_INVITE_URL } from "modules/common/constants/support";
import {
  FACEBOOK_URL,
  INSTAGRAM_URL,
  X_URL,
} from "modules/common/constants/social";
import { useAppUser } from "modules/auth/user/hooks/useAppUser";
import {
  AccountNavItem,
  useAccountNav,
} from "modules/common/components/navigation/useAccountNav";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { activeButton, clerkImage, cn } from "utils/utils";
import { DynamicThemeSwitch } from "modules/common/components/ui/DynamicThemeSwitch";
import { useFindCardsHref } from "modules/common/components/navigation/hooks/useFindCardsHref";
import React, { useEffect, useRef } from "react";

const ICON_SIZE = 18;

const menuItemClasses = cn(
  "relative",
  "text-primary",
  "hover:bg-primary hover:text-primary-content",
  "focus:bg-primary focus:text-primary-content focus:outline-hidden",
  "active:bg-primary active:text-primary-content active:outline-hidden"
);

const activeMenuItemClasses = cn(
  "bg-primary/10 font-semibold text-primary",
  "before:bg-primary before:absolute before:inset-y-2 before:left-1 before:w-0.5 before:rounded-full"
);

function closeDrawer() {
  const drawer = document.getElementById(
    "mobile-menu-drawer"
  ) as HTMLInputElement | null;
  if (drawer) drawer.checked = false;
}

function MobileMenuSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section aria-labelledby={id}>
      <h2
        id={id}
        className="text-base-content/50 px-3 pb-1 text-xs font-semibold"
      >
        {title}
      </h2>
      <ul className="menu menu-lg w-full gap-1 p-0">{children}</ul>
    </section>
  );
}

/** Renders one account nav item: link, button, submenu, or loading state */
function AccountNavEntry({
  item,
  pathName,
}: {
  item: AccountNavItem;
  pathName: string | null;
}) {
  if (item.isLoading) {
    return (
      <div className={cn(menuItemClasses, "cursor-wait")}>
        {item.icon}
        {item.text}
      </div>
    );
  }

  if (item.children) {
    return (
      <details>
        <summary className={menuItemClasses}>
          {item.icon}
          {item.text}
        </summary>
        <ul className="flex flex-col gap-1 p-2">
          {item.children.map((child) => (
            <li key={child.key}>
              <AccountNavEntry item={child} pathName={pathName} />
            </li>
          ))}
        </ul>
      </details>
    );
  }

  if (item.onClick) {
    return (
      <button
        type="button"
        onClick={item.onClick}
        aria-haspopup={item.hasPopup ? "dialog" : undefined}
        className={menuItemClasses}
      >
        {item.icon}
        {item.text}
      </button>
    );
  }

  return (
    <Link
      href={item.href!}
      prefetch={item.prefetch}
      className={cn(
        menuItemClasses,
        activeButton(pathName, item.activeHref ?? item.href!) &&
          activeMenuItemClasses
      )}
    >
      {item.badge === "dot" ? (
        <div className="indicator">
          {item.icon}
          <span className="indicator-item badge badge-xs badge-error"></span>
        </div>
      ) : (
        item.icon
      )}
      {item.text}
      {typeof item.badge === "number" && (
        <span className="badge badge-error badge-sm ml-auto">
          {item.badge > 99 ? "99+" : item.badge}
        </span>
      )}
    </Link>
  );
}

export function MobileMenu() {
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const { appUser } = useAppUser();
  const { yourStuff, settings } = useAccountNav();
  const findCardsHref = useFindCardsHref();
  const pathName = usePathname();

  // Backstop: close the drawer on any route change (covers back/forward
  // navigation; same-route taps are covered by the click delegation below).
  // Must not fire on mount - the menu lazy-mounts at the moment the drawer
  // first opens, and closing here would immediately undo that open.
  const prevPathRef = useRef(pathName);
  useEffect(() => {
    if (prevPathRef.current !== pathName) closeDrawer();
    prevPathRef.current = pathName;
  }, [pathName]);

  // Single delegated handler instead of onClick on every link: any link or
  // button tap inside the menu closes the drawer (theme switch is a label,
  // submenu toggles are <summary> - neither matches, so both keep it open)
  const handleNavClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("a,button")) closeDrawer();
  };

  return (
    <nav
      className="flex flex-1 flex-col"
      aria-label="Mobile navigation"
      onClick={handleNavClick}
    >
      {isSignedIn && (
        <div className="px-2 pt-2">
          <Link
            href={`/users/${user?.username}`}
            className="bg-base-100 hover:bg-base-300/60 focus-visible:ring-primary rounded-box flex items-center gap-3 px-3 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2"
          >
            <div className="avatar">
              <div className="ring-primary/50 w-10 rounded-full ring-1">
                <Image
                  src={clerkImage(appUser?.preferredAvatar || "", 40, 40)}
                  alt={user?.username || ""}
                  width={40}
                  height={40}
                  className="object-cover"
                />
              </div>
            </div>
            <div className="flex min-w-0 flex-col">
              <span className="text-primary truncate font-semibold">
                {user?.username}
              </span>
              <span className="text-base-content/60 text-xs">View profile</span>
            </div>
            <ChevronRight
              size={ICON_SIZE}
              className="text-base-content/35 ml-auto shrink-0"
            />
          </Link>
        </div>
      )}

      <Link href={findCardsHref} className="btn btn-primary mx-2 mt-2">
        <MapPin size={ICON_SIZE} aria-hidden="true" />
        Find cards
      </Link>

      <div className="flex flex-col gap-4 p-2">
        {isSignedIn && (
          <MobileMenuSection id="mobile-menu-your-stuff" title="Your stuff">
            {yourStuff.map((item) => (
              <li key={item.key}>
                <AccountNavEntry item={item} pathName={pathName} />
              </li>
            ))}
          </MobileMenuSection>
        )}

        <MobileMenuSection id="mobile-menu-explore" title="Explore">
          {isSignedIn ? (
            <>
              <li>
                <Link
                  href="/sets"
                  className={cn(
                    menuItemClasses,
                    activeButton(pathName, "/sets") && activeMenuItemClasses
                  )}
                >
                  <SwatchBook size={ICON_SIZE} className="-rotate-45" />
                  Cards
                </Link>
              </li>
            </>
          ) : (
            <>
              <li>
                <Link
                  href="/sets"
                  className={cn(
                    menuItemClasses,
                    activeButton(pathName, "/sets") && activeMenuItemClasses
                  )}
                >
                  <SwatchBook size={ICON_SIZE} className="-rotate-45" />
                  Cards
                </Link>
              </li>
              <li>
                <Link
                  href="/decks"
                  className={cn(
                    menuItemClasses,
                    activeButton(pathName, "/decks") && activeMenuItemClasses
                  )}
                >
                  <DecksIcon fontSize={ICON_SIZE} />
                  Decks
                </Link>
              </li>
            </>
          )}
          <li>
            <Link
              href="/scanner"
              className={cn(
                menuItemClasses,
                activeButton(pathName, "/scanner") && activeMenuItemClasses
              )}
            >
              <ScanLine size={ICON_SIZE} />
              Scanner
            </Link>
          </li>
        </MobileMenuSection>

        {isSignedIn && (
          <MobileMenuSection id="mobile-menu-account" title="Account">
            {settings.map((item) => (
              <li key={item.key}>
                <AccountNavEntry item={item} pathName={pathName} />
              </li>
            ))}
          </MobileMenuSection>
        )}

        <MobileMenuSection id="mobile-menu-help" title="Help and information">
          <li>
            <details>
              <summary className={menuItemClasses}>
                <HelpCircle size={ICON_SIZE} />
                Help & Support
              </summary>
              <ul className="flex flex-col gap-1 p-2">
                <li>
                  <Link
                    href="/help"
                    className={cn(
                      menuItemClasses,
                      activeButton(pathName, "/help") && activeMenuItemClasses
                    )}
                  >
                    Help Center
                  </Link>
                </li>
                <li>
                  <Link
                    href="/help/faq"
                    className={cn(
                      menuItemClasses,
                      activeButton(pathName, "/help/faq") &&
                        activeMenuItemClasses
                    )}
                  >
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link
                    href="/contact"
                    className={cn(
                      menuItemClasses,
                      activeButton(pathName, "/contact") &&
                        activeMenuItemClasses
                    )}
                  >
                    Contact Us
                  </Link>
                </li>
              </ul>
            </details>
          </li>
          <li>
            <details>
              <summary className={menuItemClasses}>
                <Info size={ICON_SIZE} />
                About & Legal
              </summary>
              <ul className="flex flex-col gap-1 p-2">
                <li>
                  <Link
                    href="/about"
                    className={cn(
                      menuItemClasses,
                      activeButton(pathName, "/about") && activeMenuItemClasses
                    )}
                  >
                    About Lootstack
                  </Link>
                </li>
                <li>
                  <Link
                    href="/privacy"
                    className={cn(
                      menuItemClasses,
                      activeButton(pathName, "/privacy") &&
                        activeMenuItemClasses
                    )}
                  >
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </details>
          </li>
        </MobileMenuSection>
      </div>
      {/* Auth buttons for mobile */}

      {/* Social Links */}
      <div className="mt-auto flex flex-col justify-center gap-2 p-4">
        {!isSignedIn && (
          <div className="border-base-300 border-t pt-4">
            <div className="flex flex-col gap-2">
              <SignInButton mode="modal">
                <button className="btn btn-block btn-outline">Sign In</button>
              </SignInButton>
              <SignUpButton mode="modal">
                <button className="btn btn-block btn-ghost">Sign Up</button>
              </SignUpButton>
            </div>
          </div>
        )}
        <div className="flex items-center justify-center gap-4">
          <DynamicThemeSwitch className="grid xl:hidden" />

          <Link
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-circle border-base-300 bg-base-100 text-base-content btn-sm hover:border-primary hover:bg-primary hover:text-primary-content transition-all duration-300"
          >
            <DiscordIcon className="h-5 w-5" aria-hidden="true" />
            <span className="sr-only">Discord</span>
          </Link>
          <Link
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-circle border-base-300 bg-base-100 text-base-content btn-sm hover:border-primary hover:bg-primary hover:text-primary-content transition-all duration-300"
          >
            <InstagramIcon className="h-5 w-5" />
            <span className="sr-only">Instagram</span>
          </Link>
          <Link
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-circle border-base-300 bg-base-100 text-base-content btn-sm hover:border-primary hover:bg-primary hover:text-primary-content transition-all duration-300"
          >
            <FacebookIcon className="h-5 w-5" />
            <span className="sr-only">Facebook</span>
          </Link>
          <Link
            href={X_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-circle border-base-300 bg-base-100 text-base-content btn-sm hover:border-primary hover:bg-primary hover:text-primary-content transition-all duration-300"
          >
            <XIcon className="h-5 w-5" />
            <span className="sr-only">X</span>
          </Link>
        </div>

        {/* Condensed Legal Notice */}
        <div className="text-base-content/60 flex flex-col p-2 text-center text-xs">
          <p className="mb-2">
            &copy; {new Date().getFullYear()} Lootstack. All rights reserved.
          </p>
          <p>
            All Game Assets are the intellectual property of their respective
            owners. Lootstack does not claim rights to any Game Assets on the
            Platform.
          </p>
        </div>
      </div>
    </nav>
  );
}
